(ns app.reporting.model
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.group.model :as group-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.service.spreadsheet.model :as spreadsheet-model]
            [clojure.set :as set]))

(def report-types
  #{:reporting/code-report
    :reporting/issued-permits
    :reporting/construction-started-report
    :reporting/open-permits
    :reporting/statcan-spreadsheet-report
    :reporting/statcan-occupancy-report
    :reporting/submitted-applications
    :reporting/bonds
    :reporting.escrow/balance
    :reporting.escrow/daily-payments
    :reporting.inspections/inspections
    :reporting.inspections/open-inspections
    :reporting.inspections/open-orders
    :reporting.inspections/visits
    :reporting.payment/fees-and-payments-paid
    :reporting.payment/fees-and-payments-unpaid
    :reporting.payment/online-payments-total
    :reporting.payment/online-payments-itemized
    :reporting.payment/deprecated-fees-and-payments-paid
    :reporting.payment/deprecated-fees-and-payments-unpaid
    :reporting.payment/deprecated-online-payments
    :reporting.reviews/review-events
    :reporting.reviews/reviews
    :reporting/workspaces})

(def admin-report-types
  #{:reporting.admin/users-report-full
    :reporting.admin/transaction-report
    :reporting.admin/workspace-totals-report
    :reporting.admin/workspaces-created-per-month-report
    :reporting.admin/inspector-ai
    :reporting.admin/payment-providers})

(def all-report-types
  (set/union report-types admin-report-types))

(defn is-admin-report? [report-type]
  (contains? admin-report-types report-type))

(def default-workspace-report-types
  [:reporting/submitted-applications
   :reporting/issued-permits
   :reporting/open-permits
   :reporting/construction-started-report])

(defn permit-report-types [permit-types planning-categories]
  (if (or (contains? permit-types :permit-type/B)
          (some config-model/building-permit-like? planning-categories))
    default-workspace-report-types
    (condp #(contains? %2 %1) permit-types
      :permit-type/P [:reporting/submitted-applications]
      :permit-type/L [:reporting/submitted-applications
                      :reporting/issued-permits]
      :permit-type/CR [:reporting/submitted-applications
                       :reporting/issued-permits]
      default-workspace-report-types)))

(def inspection-report-types
  [:reporting.inspections/inspections
   :reporting.inspections/visits
   :reporting.inspections/open-inspections
   :reporting.inspections/open-orders])

(def inspection-sort-options
  {:reporting.inspections/inspections      [:reporting.inspections.sort/permit-type+visit-date
                                            :reporting.inspections.sort/inspection-type]
   :reporting.inspections/visits           [:reporting.inspections.sort/visit-date
                                            :reporting.inspections.sort/inspector+visit-date]
   :reporting.inspections/open-orders      [:reporting.inspections.sort/permit-type
                                            :reporting.inspections.sort/order-issued]
   :reporting.inspections/open-inspections [:reporting.inspections.sort/permit-type+visit-date
                                            :reporting.inspections.sort/inspection-type]})

(def review-report-types
  [:reporting.reviews/reviews
   :reporting.reviews/review-events])

(def review-sort-options
  {:reporting.reviews/reviews       [:reporting.reviews.sort/permit-type+started-date
                                     :reporting.reviews.sort/name+started-date]
   :reporting.reviews/review-events [:reporting.reviews.sort/completed-date
                                     :reporting.reviews.sort/reviewer+completed-date]})

(def escrow-report-types
  [:reporting.escrow/daily-payments
   :reporting.escrow/balance])

(def online-payment-types
  [:reporting.payment/online-payments-total
   :reporting.payment/online-payments-itemized])

(def payment-optional-fields-all
  [:reporting/municipality
   :reporting/permit-type
   :reporting/work-type
   :reporting/work-target
   :reporting/work-order-number
   :reporting/reported-payment-date
   :reporting/additional-payment-info
   :reporting/note
   :reporting/bill-due-date-ts
   :payment/cheque-number])

(defn payment-optional-fields [permit-types auth-group? cheque-number-enabled?]
  (cond->> payment-optional-fields-all
    auth-group? (remove #{:reporting/permit-type})
    (contains? permit-types :permit-type/L) (remove #{:reporting/work-order-number})
    (not cheque-number-enabled?) (remove #{:payment/cheque-number})))

(def date-format-options
  [:reporting.date-format/local-datetime
   :reporting.date-format/local-date])

(defn matching-group-id? [group-id reporting-filter]
  (->> reporting-filter
       :filter/conditions
       (some #(and (= (:filter.condition/attribute %) :group-id)
                   (= (:filter.condition/values %)
                      #?(:clj  (str "#{" group-id "}")
                         :cljs #{group-id}))))))

(def permit-type->exclusive-fields
  "Any field listed here is only available for an organization whose scopes include the permit-type it is listed under.
  Any field not listed here is available for all reports of all permit types.
  Note 1: A listing for BS is not necessary because each organization that has BS also has B
  Note 2: There are no entries for cases or complaints reports since those all always have the same fields
  Note 3: This affects non-optional fields currently only in permit and review reports
  (since they use the shared frontend implementation for optional fields but their own backend implementations)"
  {:permit-type/B  {:payments #{:reporting/work-type
                                :reporting/work-target
                                :reporting/work-order-number}
                    :permits  #{:reporting/issued-date
                                :reporting/issued-by
                                :reporting/completion-date
                                :reporting/indemnity-amount
                                :reporting/builder-name
                                :reporting/builder-address
                                :reporting/builder-company
                                :reporting/permit-fees-total
                                :reporting/construction-cost
                                :reporting/area-of-work
                                :reporting/statcan-building-code
                                :reporting/statcan-work-code
                                :reporting/review-start
                                :reporting/review-end
                                :reporting/finished-date
                                :reporting/time-in-review
                                :reporting/changes-requested
                                :reporting/municipal-review
                                :reporting/construction-started-from-inspection
                                :reporting/construction-started-date}
                    :reviews  #{:reporting/requested-date
                                :reporting/completed-date
                                :reporting/time-in-review
                                :reporting/changes-requested
                                :reporting/municipal-review}}
   :permit-type/CE {:inspections #{:reporting/case-description
                                   :reporting/case-description-of-location
                                   :reporting/case-in-proximity
                                   :reporting/case-non-owner-violation}}
   :permit-type/CR {:permits #{:reporting/business-name
                               :reporting/business-trade-name
                               :reporting/issued-date
                               :reporting/valid-from-date
                               :reporting/valid-until-date}}
   :permit-type/L  {:payments #{:reporting/work-type
                                :reporting/work-target
                                :reporting/work-order-number}
                    :permits  #{:reporting/business-name
                                :reporting/business-trade-name
                                :reporting/issued-date
                                :reporting/licence-number
                                :reporting/licence-status
                                :reporting/valid-from-date
                                :reporting/valid-until-date}
                    :reviews  #{:reporting/licence-number}}
   :permit-type/LU {:payments #{:reporting/work-type
                                :reporting/work-target
                                :reporting/work-order-number}
                    :permits  #{:reporting/issued-date
                                :reporting/issued-by
                                :reporting/builder-name
                                :reporting/builder-address
                                :reporting/builder-company
                                :reporting/permit-fees-total
                                :reporting/construction-cost
                                :reporting/area-of-work
                                :reporting/review-start
                                :reporting/finished-date
                                :reporting/assignee
                                :reporting/committee-meeting-name
                                :reporting/committee-meeting-date}
                    :reviews  #{:reporting/legal-address
                                :reporting/owner
                                :reporting/issued-by}}
   :permit-type/P  {:payments #{:reporting/work-type
                                :reporting/work-target
                                :reporting/work-order-number}
                    :permits  #{:reporting/deemed-complete-date
                                :reporting/application-completed-date
                                :reporting/circulation-ended-date
                                :reporting/review-ended-date
                                :reporting/approved-date
                                :reporting/assignee
                                :reporting/draft-report-release-date
                                :reporting/draft-report-review-date
                                :reporting/ready-for-approval-date
                                :reporting/committee-meeting-name
                                :reporting/committee-meeting-date}}
   :permit-type/PW {:payments #{:reporting/work-type
                                :reporting/work-target
                                :reporting/work-order-number}
                    :permits  #{:reporting/issued-date
                                :reporting/issued-by
                                :reporting/completion-date
                                :reporting/indemnity-amount
                                :reporting/builder-name
                                :reporting/builder-address
                                :reporting/builder-company
                                :reporting/permit-fees-total
                                :reporting/construction-cost
                                :reporting/area-of-work
                                :reporting/statcan-building-code
                                :reporting/statcan-work-code}}})

(def country->exclusive-fields
  {:ca #{:reporting/statcan-building-code
         :reporting/statcan-work-code}})

(def group-type->excluded-frontend-fields
  {:group.type/superdomain #{:reporting/municipality}})

(def workspace-optional-fields-config
  "Defines optional fields for workspace reports in display order, with the permit types that enable each field."
  [[:reporting/application-unit-type-and-number #{:permit-type/B
                                                  :permit-type/CE
                                                  :permit-type/CR
                                                  :permit-type/L
                                                  :permit-type/LU
                                                  :permit-type/P
                                                  :permit-type/PW
                                                  :permit-type/PWO}]
   [:reporting/business-name #{:permit-type/L :permit-type/CR}]
   [:reporting/business-trade-name #{:permit-type/L :permit-type/CR}]
   [:reporting/licence-number #{:permit-type/L}]
   [:reporting/valid-from-date #{:permit-type/L :permit-type/CR}]
   [:reporting/valid-until-date #{:permit-type/L :permit-type/CR}]
   [:reporting/licence-status #{:permit-type/L}]
   [:reporting/completion-date #{:permit-type/B :permit-type/P :permit-type/PW}]
   [:reporting/indemnity-amount #{:permit-type/B :permit-type/P :permit-type/PW}]
   [:reporting/deemed-complete-date #{:permit-type/P}]
   [:reporting/application-completed-date #{:permit-type/P}]
   [:reporting/circulation-ended-date #{:permit-type/P}]
   [:reporting/review-ended-date #{:permit-type/P}]
   [:reporting/approved-date #{:permit-type/P}]
   [:reporting/review-start #{:permit-type/B :permit-type/P}]
   [:reporting/review-end #{:permit-type/B :permit-type/P}]
   [:reporting/construction-started-date #{:permit-type/B :permit-type/P}]
   [:reporting/construction-started-from-inspection #{:permit-type/B :permit-type/P}]
   [:workspace/case-description #{:permit-type/CE :permit-type/PWO}]
   [:reporting/case-priority #{:permit-type/CE :permit-type/PWO}]
   [:reporting/case-created #{:permit-type/CE :permit-type/PWO}]
   [:reporting/last-visit #{:permit-type/CE :permit-type/PWO}]
   [:reporting/next-booking #{:permit-type/CE :permit-type/PWO}]
   [:reporting/work-order-description #{:permit-type/PWO}]
   [:reporting/work-order-priority #{:permit-type/PWO}]
   [:reporting/work-order-created #{:permit-type/PWO}]
   [:reporting/last-task #{:permit-type/PWO}]
   [:reporting/next-task-booking #{:permit-type/PWO}]
   [:reporting/express-issuance #{:permit-type/B
                                  :permit-type/CR
                                  :permit-type/L
                                  :permit-type/LU
                                  :permit-type/P
                                  :permit-type/PW}]])

(defn workspace-optional-fields
  "Given a set of permit types, returns the optional fields available for workspace reports
   in display order."
  [permit-types]
  (->> workspace-optional-fields-config
       (filter (fn [[_ field-permit-types]]
                 (some permit-types field-permit-types)))
       (mapv first)))

(def all-country-specific-fields
  (apply set/union (vals country->exclusive-fields)))

(defn unavailable-field-for-country? [country-kw field-kw]
  (let [unavailable-fields (set/difference all-country-specific-fields
                                           (country-kw country->exclusive-fields))]
    (contains? unavailable-fields field-kw)))

(defn unavailable-field?
  "Returns true if the given reporting field is not available for the given permit-types, group type and report type"
  [country group front? permit-types report-kw field-kw]
  (let [country            (keyword country)
        get-fields         #(->> % vals (map report-kw) (apply set/union))
        excluded-fields    (set/union (get-fields permit-type->exclusive-fields)
                                      (when front? (get group-type->excluded-frontend-fields (:group/type group))))
        included-fields    (-> permit-type->exclusive-fields
                               (select-keys permit-types)
                               get-fields)
        unavailable-fields (set/difference excluded-fields included-fields)]
    (or (contains? unavailable-fields field-kw)
        (unavailable-field-for-country? country field-kw))))

(def file-formats spreadsheet-model/file-formats)

(def reporting-views
  ;; Order matters
  [;; Landing views, prioritized first
   :view/group.reporting.permits
   :view/group.reporting.workspaces
   :view/group.reporting.applications
   :view/group.reporting.licences
   :view/group.reporting.escrow
   :view/group.reporting.bonds

   :view/group.reporting.assessment
   :view/group.reporting.base
   :view/group.reporting.cases
   :view/group.reporting.code-report
   :view/group.reporting.complaints
   :view/group.reporting.inspections
   :view/group.reporting.payments
   :view/group.reporting.reviews
   :view/group.reporting.statcan-spreadsheet
   :view/group.reporting.statcan-occupancy])

(def ^:private base-reporting-view-preds
  #{:view/group.reporting.assessment
    :view/group.reporting.code-report
    :view/group.reporting.inspections
    :view/group.reporting.payments
    :view/group.reporting.reviews
    :view/group.reporting.statcan-spreadsheet
    :view/group.reporting.statcan-occupancy})

(defn- permit-type->reporting-view-pred
  [permit-type]
  (case permit-type
    (:permit-type/CE :permit-type/PWO) #{:view/group.reporting.cases
                                         :view/group.reporting.complaints
                                         :view/group.reporting.payments
                                         :view/group.reporting.inspections}

    :permit-type/L (set/union base-reporting-view-preds
                              #{:view/group.reporting.licences})

    :permit-type/LS (set/union base-reporting-view-preds
                               #{:view/group.reporting.licences})

    :permit-type/P (set/union base-reporting-view-preds
                              #{:view/group.reporting.applications})

    (set/union base-reporting-view-preds
               #{:view/group.reporting.permits})))

(def reporting-view-predicates
  {:view/group.reporting.assessment          :assessment-reporting-available?
   :view/group.reporting.payments            (fn [features]
                                               (or (:fees-and-payments? features)
                                                   (:online-payment? features)))
   :view/group.reporting.inspections         :inspections?
   :view/group.reporting.statcan-spreadsheet :report-to-statcan?
   :view/group.reporting.statcan-occupancy   :report-to-statcan?
   :view/group.reporting.code-report         :group.feature/code-report
   :view/group.reporting.escrow              :group.feature/escrow-accounts
   :view/group.reporting.bonds               :group.feature/bonds
   :view/group.reporting.reviews             :reviews? ;; Temporary check because reviews need to be checked until :feature-flag.id/superdomain-can-manage-forms is removed
   })

(defn- permit-types->reporting-views
  [permit-types superdomain?]
  (let [base-pred (->> (if superdomain?
                         (set/intersection permit-types ptc-model/superdomain-allowed-permit-types)
                         permit-types)
                       (map permit-type->reporting-view-pred)
                       (apply some-fn (constantly false)))
        pred      (if superdomain?
                    (fn [view]
                      (and (not (#{:view/group.reporting.cases
                                   :view/group.reporting.permits} view))
                           (or (= view :view/group.reporting.workspaces)
                               (base-pred view))))
                    base-pred)]
    (filter pred reporting-views)))

(defn- feature-enabled-reporting-views
  [features reporting-views]
  (->> reporting-views
       (remove (fn [view]
                 (when-let [pred (get reporting-view-predicates view)]
                   (not (pred features)))))))

(defn enabled-reporting-views
  [permit-types features domain? superdomain?]
  (if (not domain?)
    (->> (permit-types->reporting-views permit-types superdomain?)
         (feature-enabled-reporting-views features))
    (feature-enabled-reporting-views features [:view/group.reporting.escrow
                                               :view/group.reporting.bonds
                                               :view/group.reporting.payments])))

(defn group-reporting-views
  [group features]
  (let [domain?      (group-model/domain? group)
        superdomain? (group-model/superdomain? group)]
    (enabled-reporting-views (group-model/get-permit-types group) features domain? superdomain?)))

(def ^{:arglists '([authority-group])} group-reports
  (group-model/entities-of-group-fn :entity.type/report))
